import axiosInstance from "../api.axiosInstance"
import type {
  ConcertAdditionalDocumentRevisionUploadRequestDto,
  ConcertAdditionalDocumentsResponseDto,
  ConcertAdminUsersQueryDto,
  ConcertAdminUsersResponseDto,
  ConcertAssignmentUserStatusDto,
  ConcertCreateHomeownerResponseDto,
  ConcertCreateQuoteDto,
  ConcertCreateSellerUserResponseDto,
  ConcertGenerateLoanDocsResponseDto,
  ConcertLoanDocsReadinessResponseDto,
  ConcertPricingResponseDto,
  ConcertQuoteResponseDto,
  ConcertReassignUserResponseDto,
  ConcertSendLoanDocsResponseDto,
  ConcertSubmitAdditionalDocumentsForReviewRequestDto,
  ConcertSubmitApplicationInPersonDto,
  ConcertSubmitApplicationInPersonResponseDto,
  ConcertSubmitChangeOrderResponseDto,
} from "../dto/concert.dto"

export class ConcertService {
  async getPricing(proposalId: string): Promise<ConcertPricingResponseDto> {
    const response = await axiosInstance.get<ConcertPricingResponseDto>(
      `/loan-product/concert/${proposalId}/pricing-calculator`,
    )

    return response.data
  }

  async createQuote(
    proposalId: string,
    body: ConcertCreateQuoteDto,
  ): Promise<ConcertQuoteResponseDto> {
    const response = await axiosInstance.post<ConcertQuoteResponseDto>(
      `/loan-product/concert/${proposalId}/quote`,
      body,
    )

    return response.data
  }

  async submitChangeOrder(
    proposalId: string,
  ): Promise<ConcertSubmitChangeOrderResponseDto> {
    const response =
      await axiosInstance.post<ConcertSubmitChangeOrderResponseDto>(
        `/loan-product/concert/${proposalId}/change-order/submit`,
        {},
      )

    return response.data
  }

  async submitApplicationInPerson(
    proposalId: string,
    applicationData: ConcertSubmitApplicationInPersonDto,
  ): Promise<ConcertSubmitApplicationInPersonResponseDto> {
    const response =
      await axiosInstance.post<ConcertSubmitApplicationInPersonResponseDto>(
        `/loan-product/concert/${proposalId}/application/in-person`,
        applicationData,
      )

    return response.data
  }

  async generateLoanDocs(
    proposalId: string,
  ): Promise<ConcertGenerateLoanDocsResponseDto> {
    const response =
      await axiosInstance.post<ConcertGenerateLoanDocsResponseDto>(
        `/loan-product/concert/${proposalId}/generate-loan-docs`,
        {},
      )

    return response.data
  }

  async sendLoanDocs(
    proposalId: string,
  ): Promise<ConcertSendLoanDocsResponseDto> {
    const response = await axiosInstance.post<ConcertSendLoanDocsResponseDto>(
      `/loan-product/concert/${proposalId}/send-loan-docs`,
      {},
    )

    return response.data
  }

  async getLoanDocsReadiness(
    proposalId: string,
  ): Promise<ConcertLoanDocsReadinessResponseDto> {
    const response =
      await axiosInstance.get<ConcertLoanDocsReadinessResponseDto>(
        `/loan-product/concert/${proposalId}/loan-docs/readiness`,
      )

    return response.data
  }

  async getAdditionalDocuments(
    proposalId: string,
  ): Promise<ConcertAdditionalDocumentsResponseDto> {
    const response =
      await axiosInstance.get<ConcertAdditionalDocumentsResponseDto>(
        `/loan-product/concert/${encodeURIComponent(
          proposalId,
        )}/additional-documents`,
      )

    return response.data
  }

  async uploadAdditionalDocumentRevisions(
    proposalId: string,
    stepSku: string,
    docSku: string,
    body: ConcertAdditionalDocumentRevisionUploadRequestDto,
  ): Promise<{ success: true }> {
    const response = await axiosInstance.post<{ success: true }>(
      `/loan-product/concert/${encodeURIComponent(
        proposalId,
      )}/additional-documents/${encodeURIComponent(
        stepSku,
      )}/${encodeURIComponent(docSku)}/revisions`,
      body,
    )

    return response.data
  }

  async submitAdditionalDocumentsForReview(
    proposalId: string,
    body: ConcertSubmitAdditionalDocumentsForReviewRequestDto,
  ): Promise<{ success: true }> {
    const response = await axiosInstance.post<{ success: true }>(
      `/loan-product/concert/${encodeURIComponent(
        proposalId,
      )}/additional-documents/submit-for-review`,
      body,
    )

    return response.data
  }

  async resendSignatureEmail(
    proposalId: string,
  ): Promise<ConcertSendLoanDocsResponseDto> {
    const response = await axiosInstance.post<ConcertSendLoanDocsResponseDto>(
      `/loan-product/concert/${proposalId}/signature-email/resend`,
      {},
    )

    return response.data
  }

  async getUserStatus(
    proposalId: string,
  ): Promise<ConcertAssignmentUserStatusDto> {
    const response = await axiosInstance.get<ConcertAssignmentUserStatusDto>(
      `/loan-product/concert/${proposalId}/user-status`,
    )

    return response.data
  }

  async reassignUser(
    proposalId: string,
  ): Promise<ConcertReassignUserResponseDto> {
    const response = await axiosInstance.post<ConcertReassignUserResponseDto>(
      `/loan-product/concert/${proposalId}/reassign-user`,
      {},
    )

    return response.data
  }

  async createHomeowner(
    proposalId: string,
  ): Promise<ConcertCreateHomeownerResponseDto> {
    const response = await axiosInstance.post<ConcertCreateHomeownerResponseDto>(
      `/loan-product/concert/${proposalId}/create-homeowner`,
      {},
    )

    return response.data
  }

  async createSellerUser(
    userId: string,
  ): Promise<ConcertCreateSellerUserResponseDto> {
    const response =
      await axiosInstance.post<ConcertCreateSellerUserResponseDto>(
        `/loan-product/concert/users/create`,
        { userId },
      )

    return response.data
  }

  async getAdminUsers(
    query: ConcertAdminUsersQueryDto,
  ): Promise<ConcertAdminUsersResponseDto> {
    const response = await axiosInstance.get<ConcertAdminUsersResponseDto>(
      `/loan-product/concert/admin/users`,
      { params: query },
    )

    return response.data
  }
}

export const concertService = new ConcertService()
